/**
 * The v2 tile (TMMAPP-0151; visual contract v2 §6 "Tile", P:425, P:497): a `surface` box, radius 16,
 * padding 16 × 18, with a label (12, the label ink), a figure (`stat`: the body face at 600, tabular)
 * and a sub-line (12, the label ink). With `to` it is a link that opens its page; its border steps up
 * to `lineStrong` under the pointer. The figure's colour is the prototype's `brand` as text, which is
 * `link` (the guaranteed brand-coloured text role), or `moneyNegative` below zero.
 */
import type { ReactNode } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { MoneyParts } from '@/components/MoneyText';
import { resolveTokens } from '@/theme/theme';
import { labelCanvasSx } from '@/theme/textLabel';

interface V2TileProps {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  /** The page it opens; absent, the tile is text. */
  to?: string | undefined;
  /** Extra lines under the sub-line (the foreign account's "≈ {operating}"). */
  children?: ReactNode;
}

export function V2Tile({ label, value, sub, to, children }: V2TileProps) {
  const tokens = resolveTokens(useTheme());
  const has = (node: ReactNode) => node !== undefined && node !== null && node !== '';
  return (
    <Box
      {...(to !== undefined ? { component: RouterLink, to } : {})}
      data-tile=""
      sx={{
        flex: '1 1 160px',
        // A figure is never cut short: a tile is at least as wide as its figure, and the row wraps
        // (the prototype's flex row, P:425).
        minWidth: 'fit-content',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        p: '16px 18px',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: tokens.line,
        bgcolor: tokens.surface,
        color: tokens.text,
        textDecoration: 'none',
        ...labelCanvasSx(tokens, 'surface'),
        ...(to !== undefined
          ? {
              '&:hover': { borderColor: tokens.lineStrong },
              '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
            }
          : {}),
      }}
    >
      <Typography
        component="span"
        data-tile-label=""
        sx={{ fontSize: 12, lineHeight: '17px', color: tokens.textLabel('surface') }}
      >
        {label}
      </Typography>
      <Box component="span" data-tile-value="" sx={{ minWidth: 0 }}>
        {value}
      </Box>
      {has(sub) && (
        <Typography
          component="span"
          data-tile-sub=""
          sx={{ fontSize: 12, lineHeight: '17px', color: tokens.textLabel('surface') }}
        >
          {sub}
        </Typography>
      )}
      {children}
    </Box>
  );
}

/**
 * A tile's money figure (`stat`, tabular): `link` (the prototype's `brand` text), "—" when not
 * served. Below zero (contract v2 §13): a balance (`tone="balance"`, the default) is `moneyNegative`;
 * a tile of totals (`tone="total"`, Overview's Credit & lines and Loans) stays ink (`text`), signed.
 * `whole` sets an aggregate in whole units.
 */
export function TileMoney({
  minor,
  currency,
  whole = false,
  tone = 'balance',
}: {
  minor: number | null;
  currency: string | null;
  whole?: boolean;
  tone?: 'balance' | 'total';
}) {
  const tokens = resolveTokens(useTheme());
  const negative = minor !== null && minor < 0;
  const negativeColor = tone === 'total' ? tokens.text : tokens.moneyNegative;
  return (
    <Typography
      variant="stat"
      component="span"
      sx={{
        display: 'block',
        color: negative ? negativeColor : tokens.link,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {minor === null || currency === null || currency === '' ? (
        '—'
      ) : (
        <MoneyParts minor={minor} currency={currency} whole={whole} />
      )}
    </Typography>
  );
}
